'use client';

import { useCallback, useRef, useState } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';
import {
  MINDMAP_FIT_TARGET,
  type PendingMindmapFitState,
} from './mindmap-fit-state';
import type { SelectMindmapNodeInput } from './mindmap-navigation';
import type {
  MindmapFlowEdge,
  MindmapFlowNode,
  ParsedMindmap,
} from './mindmap-types';

interface UseCmarkdownmindmapRuntimeInput {
  parsed: ParsedMindmap | null;
}

export function useCmarkdownmindmapRuntime({
  parsed,
}: UseCmarkdownmindmapRuntimeInput) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    parsed?.root.id ?? null,
  );
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [
    reactFlowInstance,
    setReactFlowInstance,
  ] = useState<ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null>(
    null,
  );
  const reactFlowWrapperRef = useRef<HTMLDivElement | null>(null);
  const shouldFitSelectedNodeRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const pendingFitRef = useRef<PendingMindmapFitState>({
    target: MINDMAP_FIT_TARGET.None,
    nodeIds: [],
  });
  const setSelectedMindmapNode = useCallback((
    input: SelectMindmapNodeInput,
  ): void => {
    shouldFitSelectedNodeRef.current =
      Boolean(input.nodeId) && (input.fit ?? true);
    setSelectedNodeId(input.nodeId);
  }, []);

  return {
    didInitialFitRef,
    hoveredNodeId,
    pendingFitRef,
    reactFlowInstance,
    reactFlowWrapperRef,
    selectedNodeId,
    setHoveredNodeId,
    setReactFlowInstance,
    setSelectedMindmapNode,
    setZoomLevel,
    shouldFitSelectedNodeRef,
    zoomLevel,
  };
}
